import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatINR } from "@/lib/roles";
import { Search, Loader2, Droplets } from "lucide-react";

export default function WaterList() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const limit = 15;

  const fetchList = () => {
    setLoading(true);
    const params = { page, limit };
    if (q) params.q = q;
    api.get("/water/consumers", { params }).then((r) => {
      setItems(r.data.items || []); setTotal(r.data.total || 0);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { fetchList(); /* eslint-disable-next-line */ }, [page]);

  return (
    <div className="space-y-6" data-testid="water-list-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Module</div>
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-slate-900 dark:text-white flex items-center gap-2">
          <Droplets className="w-6 h-6 text-[#0891B2]" /> Water Tax
        </h1>
      </div>

      <Card>
        <CardContent className="p-4">
          <form onSubmit={(e) => { e.preventDefault(); setPage(1); fetchList(); }} className="relative max-w-lg">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search consumers" className="pl-9" data-testid="water-search" />
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="water-table">
                <thead className="bg-slate-50 dark:bg-slate-800 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left py-3 px-4">Consumer ID</th>
                    <th className="text-left py-3 px-4">Name</th>
                    <th className="text-left py-3 px-4 hidden md:table-cell">Meter</th>
                    <th className="text-left py-3 px-4 hidden lg:table-cell">Type</th>
                    <th className="text-right py-3 px-4">Last Reading</th>
                    <th className="text-right py-3 px-4">Dues</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((c) => (
                    <tr key={c.id} className="border-t border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <td className="py-3 px-4 font-mono text-xs">{c.consumer_id}</td>
                      <td className="py-3 px-4">
                        <div className="font-medium">{c.consumer_name}</div>
                        <div className="text-xs text-slate-500 truncate max-w-xs">{c.address}</div>
                      </td>
                      <td className="py-3 px-4 hidden md:table-cell font-mono text-xs">{c.meter_number}</td>
                      <td className="py-3 px-4 hidden lg:table-cell"><Badge variant="outline" className="capitalize">{c.connection_type}</Badge></td>
                      <td className="py-3 px-4 text-right font-mono">{c.last_reading}</td>
                      <td className="py-3 px-4 text-right font-mono">
                        {c.total_dues > 0 ? <span className="text-red-600 font-semibold">{formatINR(c.total_dues)}</span> : <span className="text-slate-400">—</span>}
                      </td>
                    </tr>
                  ))}
                  {items.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-slate-500">No water consumers found.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between text-sm">
        <div className="text-slate-500">Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}</div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
          <Button size="sm" variant="outline" disabled={page * limit >= total} onClick={() => setPage(page + 1)}>Next</Button>
        </div>
      </div>
    </div>
  );
}
